import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  Paper,
  LinearProgress,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useVideo } from '../contexts/VideoContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import JSZip from 'jszip';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface Scene {
  startTime: number;
  endTime: number;
  clipPath: string;
  id?: string;
}

const SceneDetection: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { 
    videoState, 
    setVideoState, 
    startSceneDetection, 
    stopSceneDetection,
    resetVideoState 
  } = useVideo();
  const [error, setError] = useState<string | null>(null);
  const [videoUrls, setVideoUrls] = useState<{ [key: number]: string }>({});
  const [isLoadingVideos, setIsLoadingVideos] = useState<{ [key: number]: boolean }>({});
  const [errorStates, setErrorStates] = useState<{ [key: number]: boolean }>({});
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});

  // Reset video state when component mounts
  useEffect(() => {
    resetVideoState();
  }, []);

  // Check subscription status when redirected from Stripe
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/verify-subscription?session_id=${sessionId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              // Update user data with new subscription and token balance
              window.location.reload(); // Refresh to get updated user data
            }
          }
        } catch (error) {
          console.error('Error verifying subscription:', error);
        }
        // Remove the session_id from the URL
        window.history.replaceState({}, '', '/scene-detection');
      }
    };

    checkSubscriptionStatus();
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setVideoState(prev => ({
        ...prev,
        file: selectedFile,
        preview: URL.createObjectURL(selectedFile),
        scenes: [],
        isProcessing: false,
        error: null
      }));
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!videoState.file) {
      setError('Please select a video file first');
      return;
    }

    if (!user || user.tokenBalance < 5000) {
      setError('You need at least 5,000 tokens to perform scene detection. Please upgrade your subscription to get more tokens.');
      return;
    }

    try {
      // Clean up the preview URL if it exists
      if (videoState.preview) {
        URL.revokeObjectURL(videoState.preview);
      }
      
      await startSceneDetection(videoState.file);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to process video');
    }
  };

  // Clean up preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (videoState.preview) {
        URL.revokeObjectURL(videoState.preview);
      }
    };
  }, [videoState.preview]);

  // Clean up preview URL when scenes are detected
  useEffect(() => {
    if (videoState.scenes && videoState.scenes.length > 0 && videoState.preview) {
      URL.revokeObjectURL(videoState.preview);
      setVideoState(prev => ({
        ...prev,
        preview: null
      }));
    }
  }, [videoState.scenes]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup video resources when component unmounts
      const videos = document.querySelectorAll('video');
      videos.forEach(video => {
        if (video.src.startsWith('blob:')) {
          URL.revokeObjectURL(video.src);
        }
      });
    };
  }, []);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Clean up all video blob URLs
      Object.values(videoUrls).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [videoUrls]);

  // Load videos when scenes are available
  useEffect(() => {
    let isMounted = true;
    const newVideoUrls: { [key: number]: string } = {};
    const newLoadingStates: { [key: number]: boolean } = {};
    const newErrorStates: { [key: number]: boolean } = {};

    if (videoState.scenes && videoState.scenes.length > 0) {
      const fetchBlobs = videoState.scenes.map((scene, index) => {
        newLoadingStates[index] = true;
        newErrorStates[index] = false;
        return fetch(`${import.meta.env.VITE_API_URL}${scene.clipPath}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
          .then(response => response.blob())
          .then(blob => {
            if (blob.size === 0) {
              throw new Error(`Video ${index + 1} is empty`);
            }
            const url = URL.createObjectURL(blob);
            newVideoUrls[index] = url;
          })
          .catch(() => {
            newErrorStates[index] = true;
          })
          .finally(() => {
            newLoadingStates[index] = false;
          });
      });
      Promise.all(fetchBlobs).then(() => {
        if (isMounted) {
          setVideoUrls(newVideoUrls);
          setIsLoadingVideos(newLoadingStates);
          setErrorStates(newErrorStates);
        }
      });
    }
    return () => {
      isMounted = false;
      // Clean up all blob URLs
      Object.values(newVideoUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [videoState.scenes]);

  // Update the video element rendering
  const renderVideoElement = (index: number) => {
    const isLoading = isLoadingVideos[index];
    const url = videoUrls[index];
    const hasError = errorStates[index];

    if (isLoading) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '180px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'black'
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (hasError || !url) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '180px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'black',
            color: 'white',
            flexDirection: 'column'
          }}
        >
          Failed to load video
          <Button size="small" onClick={() => retryLoadScene(index)} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Box>
      );
    }

    return (
      <video
        ref={el => (videoRefs.current[index] = el)}
        controls
        preload="metadata"
        playsInline
        style={{
          width: '100%',
          height: '180px',
          objectFit: 'cover'
        }}
        src={url}
        onError={() => {
          setErrorStates(prev => ({ ...prev, [index]: true }));
        }}
      />
    );
  };

  // Retry logic for failed scenes
  const retryLoadScene = (index: number) => {
    setIsLoadingVideos(prev => ({ ...prev, [index]: true }));
    setErrorStates(prev => ({ ...prev, [index]: false }));
    fetch(`${import.meta.env.VITE_API_URL}${videoState.scenes[index].clipPath}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(response => response.blob())
      .then(blob => {
        if (blob.size === 0) {
          throw new Error('Video is empty');
        }
        const url = URL.createObjectURL(blob);
        setVideoUrls(prev => ({ ...prev, [index]: url }));
      })
      .catch(() => {
        setErrorStates(prev => ({ ...prev, [index]: true }));
      })
      .finally(() => {
        setIsLoadingVideos(prev => ({ ...prev, [index]: false }));
      });
  };

  // Update the play button click handler
  const handlePlay = async (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      try {
        if (video.readyState < 2) {
          await new Promise<void>((resolve) => {
            video.onloadeddata = () => resolve();
          });
        }
        await video.play();
      } catch (err) {
        console.error('Failed to play video:', err);
        // If play fails, try to reload and play
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${import.meta.env.VITE_API_URL}${videoState.scenes[index].clipPath}`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
        xhr.responseType = 'blob';
        xhr.onload = function() {
          if (this.status === 200) {
            const blob = this.response;
            const url = URL.createObjectURL(blob);
            video.src = url;
            video.load();
            video.play().catch(console.error);
          }
        };
        xhr.send();
      }
    }
  };

  const handleCancel = () => {
    stopSceneDetection();
    setError(null);
  };

  const handleDownloadScenes = async () => {
    if (!videoState.scenes || !videoState.scenes.length || !videoState.file) return;

    try {
      setError(null);

      // Create a zip file
      const zip = new JSZip();
      
      // Download scenes in batches to avoid memory issues
      const batchSize = 5; // Process 5 scenes at a time
      const totalScenes = videoState.scenes.length;
      
      for (let i = 0; i < totalScenes; i += batchSize) {
        const batch = videoState.scenes.slice(i, i + batchSize);
        const batchPromises = batch.map(async (scene, index) => {
          try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}${scene.clipPath}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            
            if (!response.ok) {
              throw new Error(`Failed to download scene ${i + index + 1}: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            zip.file(`scene_${i + index + 1}.mp4`, blob);
            
            return true;
          } catch (error) {
            console.error(`Error downloading scene ${i + index + 1}:`, error);
            return false;
          }
        });
        
        await Promise.all(batchPromises);
      }
      
      const content = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 5
        }
      });
      
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'scenes.zip';
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading scenes:', error);
      setError('Failed to download scenes. Please try again.');
    }
  };

  const handleDownloadScene = async (scene: Scene, index: number) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}${scene.clipPath}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scene_${index + 1}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading scene:', error);
      setError('Failed to download scene. Please try again.');
    }
  };

  // Cancel scene detection if user leaves the page or navigates away
  useEffect(() => {
    return () => {
      stopSceneDetection();
    };
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Navbar />
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="info">
            Please sign in to access the scene detection feature.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Box sx={{ flex: 1 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Paper 
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Scene Detection
            </Typography>
            {user && (
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'primary.main', fontWeight: 500 }}>
                Token Balance: {user.tokenBalance.toLocaleString()}
              </Typography>
            )}
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Upload a video to detect and extract individual scenes. Each scene detection costs 5000 tokens.
            </Typography>
            {/* Upload Section */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                mb: 4,
                border: '2px dashed',
                borderColor: 'primary.main',
                borderRadius: 2,
                bgcolor: 'rgba(25, 118, 210, 0.04)',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="video-upload"
                />
                <label htmlFor="video-upload">
                  <Button
                    component="span"
                    variant="contained"
                    size="large"
                    startIcon={<UploadIcon />}
                    sx={{
                      mb: 2,
                      py: 1.5,
                      px: 4,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                    }}
                  >
                    Select Video
                  </Button>
                </label>
                {videoState.file && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Selected file: {videoState.file.name}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpload}
                      disabled={videoState.isProcessing}
                      sx={{ mt: 1, mb: 3 }}
                    >
                      {videoState.isProcessing ? 'Processing...' : 'Detect Scenes'}
                    </Button>
                    
                    {/* Video Preview */}
                    {videoState.file && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'black',
                        overflow: 'hidden',
                        mt: 3,
                      }}
                    >
                      <video
                        controls
                        style={{
                          width: '100%',
                          maxHeight: '400px',
                          borderRadius: 1,
                        }}
                        src={videoState.preview || undefined}
                      />
                    </Paper>
                    )}
                  </Box>
                )}
                {!videoState.file && (
                  <>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      or drag and drop your video file here
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supported formats: MP4, MOV, AVI, MKV
                    </Typography>
                  </>
                )}
              </Box>
            </Paper>

            {/* Error Display */}
            {(error || videoState.error) && (
              <Alert 
                severity="error" 
                sx={{ mb: 3 }}
                onClose={() => {
                  setError(null);
                  setVideoState(prev => ({ ...prev, error: null }));
                }}
              >
                {error || videoState.error}
              </Alert>
            )}

            {/* Processing Status */}
            {videoState.isProcessing && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                {/* Upload Progress */}
                {videoState.progress.stage === 'uploading' && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CircularProgress 
                        size={24} 
                        sx={{ 
                          mr: 2,
                          color: 'primary.main',
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': {
                              transform: 'scale(1)',
                              opacity: 1,
                            },
                            '50%': {
                              transform: 'scale(1.1)',
                              opacity: 0.8,
                            },
                            '100%': {
                              transform: 'scale(1)',
                              opacity: 1,
                            },
                          },
                        }} 
                      />
                      <Typography variant="h6" sx={{ fontWeight: 500, flexGrow: 1 }}>
                        Uploading Video
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        {Math.round(videoState.progress.progress)}%
                      </Typography>
                    </Box>
                    
                    <LinearProgress 
                      variant="determinate" 
                      value={videoState.progress.progress} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        mb: 2,
                        '& .MuiLinearProgress-bar': {
                          transition: 'transform 0.5s ease-in-out'
                        }
                      }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
                        variant="caption" 
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                        }}
                      >
                        {videoState.progress.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                        }}
                      >
                        {videoState.progress.details || 'Calculating...'}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancel}
                        size="small"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          ml: 2,
                          '&:hover': {
                            bgcolor: 'error.light',
                            color: 'white',
                          },
                        }}
                      >
                        Cancel Processing
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Scene Detection Progress */}
                {videoState.progress.stage !== 'uploading' && (
                  <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CircularProgress 
                    size={24} 
                    sx={{ 
                      mr: 2,
                      color: 'primary.main',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%': {
                          transform: 'scale(1)',
                          opacity: 1,
                        },
                        '50%': {
                          transform: 'scale(1.1)',
                          opacity: 0.8,
                        },
                        '100%': {
                          transform: 'scale(1)',
                          opacity: 1,
                        },
                      },
                    }} 
                  />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {videoState.progress.message || 'Processing Video'}
                  </Typography>
                </Box>
                
                {videoState.progress.details && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      fontStyle: 'italic',
                      opacity: 0.8,
                    }}
                  >
                    {videoState.progress.details}
                  </Typography>
                )}
                
                <LinearProgress 
                  variant="determinate" 
                  value={videoState.progress.progress} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    mb: 2,
                    '& .MuiLinearProgress-bar': {
                      transition: 'transform 0.5s ease-in-out'
                    }
                  }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography 
                    variant="caption" 
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      '&::before': {
                        content: '""',
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        mr: 1,
                        animation: 'pulse 1.5s ease-in-out infinite',
                      },
                    }}
                  >
                    {videoState.progress.stage === 'initializing' && 'Setting up scene detection...'}
                    {videoState.progress.stage === 'detecting_scenes' && 'Analyzing video for scene changes...'}
                    {videoState.progress.stage === 'processing_results' && 'Processing detected scenes...'}
                    {videoState.progress.stage === 'extracting_clips' && 'Extracting scene clips...'}
                    {videoState.progress.stage === 'cleaning_up' && 'Finalizing and cleaning up...'}
                    {videoState.progress.stage === 'complete' && 'Scene detection complete!'}
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancel}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'white',
                      },
                    }}
                  >
                    Cancel Processing
                  </Button>
                </Box>
                  </Box>
                )}
              </Paper>
            )}

            {/* Detected Scenes */}
            {videoState.scenes && videoState.scenes.length > 0 && (
              <Box>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 3 
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Detected Scenes
                  </Typography>
                  
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadScenes}
                    disabled={videoState.isProcessing}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                    }}
                  >
                    {videoState.isProcessing ? 'Downloading...' : 'Download All Scenes'}
                  </Button>
                </Box>
                
                {/* Scene Previews Grid */}
                <Grid container spacing={3}>
                  {videoState.scenes.map((scene, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          borderRadius: 2,
                          overflow: 'hidden',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                          },
                        }}
                      > 
                        <Box sx={{ position: 'relative', bgcolor: 'black' }}>
                          {renderVideoElement(index)}
                        </Box>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div" gutterBottom>
                            Scene {index + 1}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Duration: {Math.round((scene.endTime - scene.startTime) * 10) / 10}s
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Start: {Math.round(scene.startTime * 10) / 10}s
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button 
                            size="small" 
                            startIcon={<PlayArrowIcon />}
                            onClick={() => handlePlay(index)}
                            disabled={isLoadingVideos[index]}
                          >
                            Play
                          </Button>
                          <Button 
                            size="small" 
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadScene(scene, index)}
                          >
                            Download
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default SceneDetection; 